import { create } from 'zustand';
import type { ToastMessage } from '../interfaces/toast-message';

// Port of src/app/services/toast.service.ts.
const DEFAULT_TOAST_DURATION = 4000;

type ToastType = ToastMessage['type'];

interface ToastState {
  toasts: ToastMessage[];
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

function showToast(
  set: (fn: (state: ToastState) => Partial<ToastState>) => void,
  get: () => ToastState,
  message: string,
  type: ToastType,
  duration: number
) {
  const hasDuplicateMessage = get().toasts.some(toast =>
    toast.message === message && toast.type === type
  );
  if (hasDuplicateMessage) {
    return;
  }

  const newToast: ToastMessage = {
    id: Date.now().toString(36) + Math.random().toString(36).substring(2),
    message,
    type,
    expiresAt: Date.now() + duration
  };

  set(state => ({ toasts: [...state.toasts, newToast] }));
}

export const useToastStore = create<ToastState>()((set, get) => ({
  toasts: [],
  success: (message, duration = DEFAULT_TOAST_DURATION) => showToast(set, get, message, 'success', duration),
  error: (message, duration = DEFAULT_TOAST_DURATION) => showToast(set, get, message, 'error', duration),
  info: (message, duration = DEFAULT_TOAST_DURATION) => showToast(set, get, message, 'info', duration),
  warning: (message, duration = DEFAULT_TOAST_DURATION) => showToast(set, get, message, 'warning', duration),
  removeToast: (id) => set(state => ({ toasts: state.toasts.filter(toast => toast.id !== id) })),
}));
